import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { lazy, Suspense, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProductsRealtime } from "./hooks/useProducts";
import { useCategoriesRealtime } from "./hooks/useCategories";
import { useCMSRealtime } from "./hooks/useCMSContent";
import { useBrandsRealtime } from "./hooks/useBrands";
import { useVariantsRealtime } from "./hooks/useProductVariants";

/* ── Critical (above-fold) imports ── */
import AdminGuard from "@/components/AdminGuard";
import AdminLayout from "@/components/AdminLayout";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
const InstallBanner = lazy(() => import("@/components/InstallBanner"));
import ScrollToTop from "@/components/ScrollToTop";
import Index from "./pages/Index";
import MaintenanceMode from "./components/MaintenanceMode";
import { GlobalScripts } from "./components/GlobalScripts";

/* ── Lazy-loaded routes ── */
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const SellDevice = lazy(() => import("./pages/SellDevice"));
const BulkOrders = lazy(() => import("./pages/BulkOrders"));
const RepairRequest = lazy(() => import("./pages/RepairRequest"));
const RepairTrack = lazy(() => import("./pages/RepairTrack"));
const WhyRefurbished = lazy(() => import("./pages/WhyRefurbished"));
const CertificationProcess = lazy(() => import("./pages/CertificationProcess"));
const EMICalculator = lazy(() => import("./pages/EMICalculator"));
const Auth = lazy(() => import("./pages/Auth"));
const Signup = lazy(() => import("./pages/Signup"));
const MyAccount = lazy(() => import("./pages/MyAccount"));
const ReturnPolicy = lazy(() => import("./pages/ReturnPolicy"));
const WarrantyPolicy = lazy(() => import("./pages/WarrantyPolicy"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Install = lazy(() => import("./pages/Install"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const BlogSeeder = lazy(() => import("./pages/BlogSeeder"));

const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminRepair = lazy(() => import("./pages/AdminRepair"));
const AdminBuyback = lazy(() => import("./pages/AdminBuyback"));
const AdminBuybackSettings = lazy(() => import("./pages/AdminBuybackSettings"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminInventory = lazy(() => import("./pages/AdminInventory"));
const AdminCMS = lazy(() => import("./pages/AdminCMS"));
const AdminSEO = lazy(() => import("./pages/AdminSEO"));
const AdminBlogs = lazy(() => import("./pages/AdminBlogs"));
const AdminBlogEdit = lazy(() => import("./pages/AdminBlogEdit"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminCoupons = lazy(() => import("./pages/AdminCoupons"));
const AdminCategories = lazy(() => import("./pages/AdminCategories"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminHappyCustomers = lazy(() => import("./pages/AdminHappyCustomers"));
const ChatBot = lazy(() => import("./components/ChatBot"));

const BrandPage = lazy(() => import("./pages/BrandPage"));

/**
 * Defer realtime subscriptions until after initial render via requestIdleCallback.
 * This avoids long tasks on the main thread during page load.
 */
const RealtimeSubscriptions = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = 'requestIdleCallback' in window
      ? (window as any).requestIdleCallback(() => setReady(true), { timeout: 4000 })
      : setTimeout(() => setReady(true), 2000);
    return () => {
      if ('cancelIdleCallback' in window) (window as any).cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, []);

  if (!ready) return null;
  return <RealtimeSubscriptionsInner />;
};

const RealtimeSubscriptionsInner = () => {
  useProductsRealtime();
  useCategoriesRealtime();
  useCMSRealtime();
  useBrandsRealtime();
  useVariantsRealtime();
  return null;
};

const queryClient = new QueryClient();

/* Minimal loading fallback — no layout shift */
const PageFallback = () => (
  <div className="flex min-h-[40vh] items-center justify-center">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

/* Performance: Delay rendering of heavy off-screen elements like chatbots/popups */
const DelayedRender = ({ children, delay = 3000 }: { children: React.ReactNode, delay?: number }) => {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShouldRender(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return shouldRender ? <>{children}</> : null;
};

const MaintenanceGate = ({ children }: { children: React.ReactNode }) => {
  const { data: isMaintenanceOn } = useQuery({
    queryKey: ["maintenance-mode"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "maintenance_mode")
        .maybeSingle();
      return data?.setting_value === "true";
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const { data: isAdmin } = useQuery({
    queryKey: ["maintenance-admin-check"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;
      const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
      return !!data;
    },
    staleTime: 60_000,
  });

  const location = useLocation();
  const isAuthOrAdmin = location.pathname.startsWith("/auth") || location.pathname.startsWith("/admin");

  // Allow access to auth pages so admins can log in
  if (isMaintenanceOn && !isAdmin && !isAuthOrAdmin) {
    return <MaintenanceMode />;
  }
  
  return <>{children}</>;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <GlobalScripts />
      <TooltipProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <ScrollToTop />
            <RealtimeSubscriptions />
            {/* Defer toast containers to reduce initial main-thread work */}
            <Suspense fallback={null}>
              <DelayedRender delay={1500}>
                <Toaster />
                <Sonner />
              </DelayedRender>
            </Suspense>
            <Routes>
              {/* Admin Routes */}
              <Route path="/admin" element={
                <MaintenanceGate>
                  <AdminGuard><AdminLayout /></AdminGuard>
                </MaintenanceGate>
              }>
                <Route path="dashboard" element={<Suspense fallback={<PageFallback />}><AdminDashboard /></Suspense>} />
                <Route path="products" element={<Suspense fallback={<PageFallback />}><AdminProducts /></Suspense>} />
                <Route path="categories" element={<Suspense fallback={<PageFallback />}><AdminCategories /></Suspense>} />
                <Route path="orders" element={<Suspense fallback={<PageFallback />}><AdminOrders /></Suspense>} />
                <Route path="customers" element={<Suspense fallback={<PageFallback />}><AdminUsers /></Suspense>} />
                <Route path="inventory" element={<Suspense fallback={<PageFallback />}><AdminInventory /></Suspense>} />
                <Route path="analytics" element={<Suspense fallback={<PageFallback />}><AdminAnalytics /></Suspense>} />
                <Route path="repair" element={<Suspense fallback={<PageFallback />}><AdminRepair /></Suspense>} />
                <Route path="buyback" element={<Suspense fallback={<PageFallback />}><AdminBuyback /></Suspense>} />
                <Route path="buyback-settings" element={<Suspense fallback={<PageFallback />}><AdminBuybackSettings /></Suspense>} />
                <Route path="cms" element={<Suspense fallback={<PageFallback />}><AdminCMS /></Suspense>} />
                <Route path="seo" element={<Suspense fallback={<PageFallback />}><AdminSEO /></Suspense>} />
                <Route path="blogs" element={<Suspense fallback={<PageFallback />}><AdminBlogs /></Suspense>} />
                <Route path="blogs/:id" element={<Suspense fallback={<PageFallback />}><AdminBlogEdit /></Suspense>} />
                <Route path="coupons" element={<Suspense fallback={<PageFallback />}><AdminCoupons /></Suspense>} />
                <Route path="support" element={<Suspense fallback={<PageFallback />}><AdminSupport /></Suspense>} />
                <Route path="leads" element={<Suspense fallback={<PageFallback />}><AdminLeads /></Suspense>} />
                <Route path="happy-customers" element={<Suspense fallback={<PageFallback />}><AdminHappyCustomers /></Suspense>} />
                <Route path="settings" element={<Suspense fallback={<PageFallback />}><AdminSettings /></Suspense>} />
              </Route>

              {/* Public Routes */}
              <Route path="*" element={
                <MaintenanceGate>
                  <div className="flex min-h-screen flex-col">
                    <Navbar />
                    <main className="flex-1">
                      <Suspense fallback={<PageFallback />}>
                        <Routes>
                          <Route path="/" element={<Index />} />
                          <Route path="/products" element={<Products />} />
                          <Route path="/brand/:slug" element={<BrandPage />} />
                          <Route path="/product/:id" element={<ProductDetail />} />
                          <Route path="/cart" element={<Cart />} />
                          <Route path="/checkout" element={<Checkout />} />
                          <Route path="/sell" element={<SellDevice />} />
                          <Route path="/bulk-order" element={<BulkOrders />} />
                          <Route path="/repair" element={<RepairRequest />} />
                          <Route path="/repair/track" element={<RepairTrack />} />
                          <Route path="/why-refurbished" element={<WhyRefurbished />} />
                          <Route path="/certification" element={<CertificationProcess />} />
                          <Route path="/emi-calculator" element={<EMICalculator />} />
                          <Route path="/auth" element={<Auth />} />
                          <Route path="/signup" element={<Signup />} />
                          <Route path="/account" element={<MyAccount />} />
                          <Route path="/refund_returns" element={<ReturnPolicy />} />
                          <Route path="/warranty-policy" element={<WarrantyPolicy />} />
                          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                          <Route path="/terms" element={<TermsConditions />} />
                          <Route path="/about-us" element={<AboutUs />} />
                          <Route path="/contact-us" element={<ContactUs />} />
                          <Route path="/blog" element={<BlogIndex />} />
                          <Route path="/blog/:slug" element={<BlogDetail />} />
                          <Route path="/blog-seeder" element={<BlogSeeder />} />
                          <Route path="/install" element={<Install />} />
                          <Route path="/maintenance-mode" element={<MaintenanceMode />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </main>
                    <Footer />
                    <BottomNav />
                    <Suspense fallback={null}><InstallBanner /></Suspense>
                    <Suspense fallback={null}>
                      <DelayedRender delay={3500}>
                        <ChatBot />
                      </DelayedRender>
                    </Suspense>
                  </div>
                </MaintenanceGate>
              } />
            </Routes>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
